import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ArticleCard from '@/components/ArticleCard';
import Newsletter from '@/components/Newsletter';
import AdSpace from '@/components/AdSpace';
import { getArticles } from '@/lib/api';
import { Calendar, Loader2 } from 'lucide-react';
import { PostPayPromoCard } from '@/components/PostPayPromoCard';
import { formatEasternDayLabel, getEasternTodayKey, toEasternDateKey } from '@/lib/storyDates';

const BATCH_SIZE = 12;

const CategoryPage = () => {
  const { category } = useParams();
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const sentinelRef = useRef(null);

  // Convert URL slug to category name
  const categoryName = category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const response = await getArticles({ category: categoryName, limit: 500 });
        setAllArticles([...response.data].sort((a, b) => new Date(b.published_at) - new Date(a.published_at)));
        setVisibleCount(BATCH_SIZE);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [categoryName]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, allArticles.length));
  }, [allArticles.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const visibleArticles = allArticles.slice(0, visibleCount);
  const hasMore = visibleCount < allArticles.length;
  const todayKey = getEasternTodayKey();
  const totalTodayStories = allArticles.filter(
    (article) => article.published_at && toEasternDateKey(article.published_at) === todayKey
  ).length;
  const visibleTodayStories = visibleArticles.filter(
    (article) => article.published_at && toEasternDateKey(article.published_at) === todayKey
  ).length;
  const showPostPayToday = totalTodayStories > 0 && visibleTodayStories === totalTodayStories;
  const dayGroups = useMemo(() => {
    const groups = [];
    visibleArticles.forEach((article) => {
      const key = article.published_at ? toEasternDateKey(article.published_at) : 'Unknown';
      if (!groups.length || groups[groups.length - 1].key !== key) {
        groups.push({ key, articles: [] });
      }
      groups[groups.length - 1].articles.push(article);
    });
    return groups;
  }, [visibleArticles]);

  return (
    <div className="min-h-screen bg-white" data-testid="category-page">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Category Header */}
        <section className="mb-8 border-b border-border pb-6">
          <span className="category-label text-[hsl(var(--accent-red))]">Category</span>
          <h1 className="text-5xl font-semibold mt-2">{categoryName}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Latest {categoryName.toLowerCase()} news and insights from Cincinnati and beyond.
          </p>
        </section>

        {/* Ad Banner */}
        <section className="mb-8">
          <AdSpace size="banner" type="header" className="w-full" />
        </section>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {dayGroups.length > 0 ? (
                <>
                  {dayGroups.map((group) => (
                    <section
                      key={group.key}
                      className="mb-8"
                      data-testid={group.key === todayKey ? 'today-stories-group' : `category-day-group-${group.key.replace(/\//g, '-')}`}
                    >
                      <div className="mb-3 flex w-full items-center gap-2 border-b border-border py-2" data-testid={`category-day-header-${group.key.replace(/\//g, '-')}`}>
                        <Calendar size={14} className="text-yellow-500" />
                        <span className="text-sm font-bold text-gray-900">{formatEasternDayLabel(group.key)}</span>
                        <span className="ml-auto text-[10px] text-gray-500">{group.articles.length} {group.articles.length === 1 ? 'story' : 'stories'}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5 sm:gap-3 lg:gap-4" data-testid={`category-day-grid-${group.key.replace(/\//g, '-')}`}>
                        {group.articles.map((article, index) => (
                          <ArticleCard key={article.id} article={article} index={index} />
                        ))}
                        {group.key === todayKey && showPostPayToday && <PostPayPromoCard />}
                      </div>
                    </section>
                  ))}
                  {hasMore && (
                    <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="category-load-more-sentinel">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 border border-border">
                  <p className="text-muted-foreground">
                    No articles in this category yet. Check back soon!
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-8">
              <AdSpace size="sidebar" type="sidebar" />
              <Newsletter />
            </aside>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;
